import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toStorySlug } from '../../../../lib/slugify';

interface Story {
  id: string;
  title: string;
  author_id: string;
  genre: string;
  cover_image: string;
  author?: {
    display_name: string;
  };
  bookmarked_at?: string;
  rating?: number | null;
}

export default function ReadingListTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookmarkedStories = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('bookmarks')
          .select(`
            created_at,
            story_id,
            stories (
              id,
              title,
              author_id,
              genre,
              cover_image,
              profiles:author_id (
                display_name
              )
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Fetch ratings for each story
        const storyIds = data
          .filter(item => item.stories)
          .map(item => item.stories.id);

        const { data: ratingsData } = await supabase
          .from('reading_history')
          .select('story_id, rating')
          .eq('user_id', user.id)
          .in('story_id', storyIds);

        const ratingsMap = new Map(
          ratingsData?.map(r => [r.story_id, r.rating]) || []
        );

        const formattedStories = data
          .filter(item => item.stories)
          .map(item => ({
            id: item.stories.id,
            title: item.stories.title,
            author_id: item.stories.author_id,
            genre: item.stories.genre,
            cover_image: item.stories.cover_image,
            author: item.stories.profiles,
            bookmarked_at: item.created_at,
            rating: ratingsMap.get(item.stories.id) || null
          }));

        setStories(formattedStories);
      } catch (error) {
        console.error('Error fetching bookmarked stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarkedStories();
  }, [user?.id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleStoryClick = (story: Story) => {
    const slug = toStorySlug(story.title, story.id);
    navigate(`/story/${slug}`);
  };

  const handleRemoveBookmark = async (storyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('user_id', user.id)
        .eq('story_id', storyId);

      if (error) throw error;

      setStories(stories.filter(s => s.id !== storyId));
    } catch (error) {
      console.error('Error removing bookmark:', error);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-zinc-950 border border-red-900/30 rounded-lg overflow-hidden animate-pulse">
            <div className="w-full h-80 bg-zinc-900"></div>
            <div className="p-5">
              <div className="h-6 bg-zinc-900 rounded mb-2"></div>
              <div className="h-4 bg-zinc-900 rounded w-1/2 mb-3"></div>
              <div className="flex items-center justify-between">
                <div className="h-6 bg-zinc-900 rounded w-20"></div>
                <div className="h-4 bg-zinc-900 rounded w-24"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="text-center py-16">
        <i className="ri-bookmark-line text-6xl text-zinc-700 mb-4"></i>
        <h3 className="text-xl font-bold text-zinc-400 mb-2">No bookmarked stories yet</h3>
        <p className="text-zinc-500">Stories you bookmark will appear here</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {stories.map((story) => (
        <div
          key={story.id}
          onClick={() => handleStoryClick(story)}
          className="bg-zinc-950 border border-red-900/30 rounded-lg overflow-hidden hover:border-red-900/60 transition-colors cursor-pointer group relative"
        >
          <button
            onClick={(e) => handleRemoveBookmark(story.id, e)}
            className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/60 hover:bg-red-900/80 rounded-full transition-colors"
            title="Remove bookmark"
          >
            <i className="ri-bookmark-fill text-red-400"></i>
          </button>

          <div className="w-full h-80 flex items-center justify-center overflow-hidden">
            <img
              src={story.cover_image}
              alt={story.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          
          <div className="p-5">
            <h3 className="text-xl font-bold text-red-50 mb-2 font-serif group-hover:text-red-400 transition-colors">
              {story.title}
            </h3>
            
            <p className="text-zinc-400 text-sm mb-3">by {story.author?.display_name || 'Unknown'}</p>
            
            {story.rating && (
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i
                    key={star}
                    className={`text-lg ${
                      star <= story.rating!
                        ? 'ri-star-fill text-yellow-500'
                        : 'ri-star-line text-gray-600'
                    }`}
                  ></i>
                ))}
                <span className="text-zinc-400 text-xs ml-1">Your rating</span>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 bg-red-900/20 border border-red-900/50 rounded-full text-red-400 text-xs font-medium">
                {story.genre}
              </span>
              
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <i className="ri-bookmark-fill text-red-400"></i>
                {story.bookmarked_at && formatDate(story.bookmarked_at)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}